import { useState, useEffect } from 'react';
import { Loader2, Droplets, TrendingDown, Zap, Activity } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

import { ProgressHeader } from '@/components/progress/ProgressHeader';
import { WeeklySummaryHero } from '@/components/progress/WeeklySummaryHero';
import { WeeklyConsistencyCard, DayConsistency } from '@/components/progress/WeeklyConsistencyCard';
import { MetricTrendCard } from '@/components/progress/MetricTrendCard';
import { WeeklyTrendsChart } from '@/components/progress/WeeklyTrendsChart';
import { ProtocolProgressCard } from '@/components/progress/ProtocolProgressCard';
import { ProgressInsightCard } from '@/components/progress/ProgressInsightCard';
import { AchievementsSection, Achievement } from '@/components/progress/AchievementsSection';
import { NextStepCard } from '@/components/progress/NextStepCard';

import { ProgressSummaryCards } from '@/components/progress/ProgressSummaryCards';

const Progress = () => {
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [progressSummary, setProgressSummary] = useState<any>(null);
  const [waterTrend, setWaterTrend] = useState<any[]>([]);
  const [energyTrend, setEnergyTrend] = useState<any[]>([]);
  
  const [stats, setStats] = useState({
    water: { val: '0 vasos', trend: '-', int: '' },
    bloating: { val: 'Ninguna', trend: '-', int: '' },
    energy: { val: 'Baja', trend: '-', int: '' },
    bowel: { val: 'Regular', trend: '-', int: '' },
  });

  const [protocolData, setProtocolData] = useState({ daysDone: 0, totalDays: 21, currentPhase: 1, nextMilestone: 7, resetCompleted: true });
  const [insightMessage, setInsightMessage] = useState('');
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [nextSteps, setNextSteps] = useState<string[]>([]);

  useEffect(() => {
    const fetchProgressData = async () => {
      if (!user || !user.id) return;

      try {
        const [summaryRes, historyRes] = await Promise.all([
          fetch('/api/progress/summary'),
          fetch('/api/progress/history')
        ]);
        
        const summary = await summaryRes.json();
        const { data: checkins } = await historyRes.json();

        setProgressSummary(summary);

        const eMap: any = { "Baja": 3, "Moderada": 6, "Buena": 8, "Excelente": 10 };
        const wTrend: any[] = [];
        const eTrend: any[] = [];
        const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
        const today = new Date();

        for (let i = 6; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const dateStr = d.toLocaleDateString('en-CA');
          const dayCheckin = checkins?.find((c: any) => c.checkin_date === dateStr);
          wTrend.push({ day: days[d.getDay()][0], value: dayCheckin ? Math.min(10, dayCheckin.water_glasses) : 0 });
          eTrend.push({ day: days[d.getDay()][0], value: dayCheckin ? (eMap[dayCheckin.energy_level || "Baja"]) : 0 });
        }
        setWaterTrend(wTrend);
        setEnergyTrend(eTrend);

        const todayData = checkins?.[0];
        const yesterdayData = checkins?.[1];
        const waterDiff = (todayData?.water_glasses || 0) - (yesterdayData?.water_glasses || 0);
        const waterTrendTxt = yesterdayData ? (waterDiff > 0 ? `+${waterDiff} vs ayer` : waterDiff < 0 ? `${waterDiff} vs ayer` : "Igual que ayer") : "";
        
        setStats({
          water: { val: `${todayData?.water_glasses || 0} vasos`, trend: waterTrendTxt, int: '' },
          bloating: { val: todayData?.bloating_level || 'Ninguna', trend: '', int: todayData?.bloating_level === "Alta" ? "Priorizar comidas ligeras" : "Todo bajo control" },
          energy: { val: todayData?.energy_level || 'Baja', trend: '', int: todayData?.energy_level === "Baja" ? "Necesitas más descanso" : "Buena reserva" },
          bowel: { val: todayData?.poop_quality || 'No fui al baño', trend: '', int: todayData?.poop_quality?.includes("No fui") ? "Aumentar fibra y agua" : "Digestión fluyendo" },
        });

        const pDone = summary.totalCompletedDays;
        setProtocolData({
          daysDone: pDone,
          totalDays: 21,
          currentPhase: Math.min(3, Math.ceil((pDone + 1) / 7)),
          nextMilestone: Math.ceil((pDone + 1) / 7) * 7,
          resetCompleted: summary.resetCompleted
        });

        setAchievements([
          { title: "3 días de consistencia", icon: "🔥", achieved: summary.weeklyCompleted >= 3, progress: { current: summary.weeklyCompleted, total: 3 } },
          { title: "7 check-ins completados", icon: "📝", achieved: summary.weeklyCompleted >= 7, progress: { current: summary.weeklyCompleted, total: 7 } },
          { title: "1 semana de protocolo", icon: "🗓️", achieved: pDone >= 7, progress: { current: Math.min(7, pDone), total: 7 } },
        ]);

        if ((todayData?.water_glasses || 0) > 4 && todayData?.bloating_level === "Ninguna") {
          setInsightMessage("Notamos que los días que bebes más agua, tu hinchazón desaparece. ¡Excelente conexión!");
        } else {
          setInsightMessage("A medida que registres más días, verás conexiones sorprendentes entre tus hábitos y cómo te sientes.");
        }

        const tasks = [];
        if (!summary.resetCompleted) tasks.push("Completar Reset de 3 Días");
        if ((todayData?.water_glasses || 0) < 6) tasks.push("Beber 2 vasos de agua al despertar");
        tasks.push("Completar tu check-in de mañana");
        setNextSteps(tasks);

      } catch (err) {
        console.error("Error fetching progress data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProgressData();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-md mx-auto space-y-6 animate-fade-in pb-10">
      <ProgressHeader />
      
      <ProgressSummaryCards
        streakDays={progressSummary?.streakDays || 0}
        weeklyCompleted={progressSummary?.weeklyCompleted || 0}
        weeklyTarget={progressSummary?.weeklyTarget || 7}
        totalCompletedDays={progressSummary?.totalCompletedDays || 0}
        programTargetDays={progressSummary?.programTargetDays || 21}
        activeAlerts={progressSummary?.activeAlerts || 0}
      />

      <WeeklySummaryHero 
        completedCount={progressSummary?.weeklyCompleted || 0} 
        streak={progressSummary?.streakDays || 0} 
        bestSignal={progressSummary?.bestSignal || ""} 
        focusArea={progressSummary?.currentFocus || ""} 
      />

      <WeeklyConsistencyCard weekData={progressSummary?.consistency || []} />

      <div className="grid grid-cols-2 gap-3">
        <MetricTrendCard 
          icon={Droplets} 
          label="Hidratación" 
          value={stats.water.val} 
          trendText={stats.water.trend} 
          colorClass="text-wellness-blue"
          bgClass="bg-wellness-blue/10"
        />
        <MetricTrendCard 
          icon={TrendingDown} 
          label="Hinchazón" 
          value={stats.bloating.val} 
          trendText="" 
          interpretation={stats.bloating.int}
          colorClass="text-emerald-500"
          bgClass="bg-emerald-50"
        />
        <MetricTrendCard 
          icon={Zap} 
          label="Energía" 
          value={stats.energy.val} 
          trendText="" 
          interpretation={stats.energy.int}
          colorClass="text-wellness-peach-dark"
          bgClass="bg-wellness-peach/20"
        />
        <MetricTrendCard 
          icon={Activity} 
          label="Intestino" 
          value={stats.bowel.val} 
          trendText="" 
          interpretation={stats.bowel.int}
          colorClass="text-amber-500"
          bgClass="bg-amber-50"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WeeklyTrendsChart data={waterTrend} title="Hidratación Semanal" colorClass="bg-wellness-blue" />
        <WeeklyTrendsChart data={energyTrend} title="Energía Semanal" colorClass="bg-wellness-peach-dark" />
      </div>

      <ProtocolProgressCard {...protocolData} />

      <ProgressInsightCard message={insightMessage} />

      <AchievementsSection achievements={achievements} />

      <div className="pt-4">
        <NextStepCard tasks={nextSteps} />
      </div>
    </div>
  );
};

export default Progress;